import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// src/plugins/builder/templates/export-google-sheets/generator.tsx
// Ships every new review as a JSON row to a Zapier catch-hook, which routes to a
// Google Sheet. Also provides a "Send last 50 now" button on the UI page.
import meta from './template.json';
import { manifestFor, header, readme, assemble, safeString } from '../_helpers';
import { useState } from 'react';
import { useReviews, useToast } from '../../../sdk';
import { Button } from '../../../../ui/Button';
const RuntimeUi = (ctx) => {
    const cfg = ctx.config;
    return _jsx(ExportSheetUi, { name: ctx.instanceName, cfg: cfg });
};
function ExportSheetUi({ name, cfg }) {
    const { reviews } = useReviews({ limit: 50 });
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    const [count, setCount] = useState(null);
    async function sendAll() {
        setBusy(true);
        try {
            const rows = reviews ?? [];
            for (const r of rows) {
                await fetch(cfg.webhookUrl, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json' },
                    body: JSON.stringify({
                        id: r.id, platform: r.platform, author: r.author_name,
                        rating: r.rating, body: r.body, posted_at: r.posted_at,
                        source: 'plugin:builder:export-google-sheets',
                    }),
                }).catch(() => { });
            }
            setCount(rows.length);
            toast.success(`Sent ${rows.length} rows to ${cfg.sheetName}.`);
        }
        catch (e) {
            const m = e instanceof Error ? e.message : "Couldn't reach the webhook.";
            toast.error(m);
        }
        finally {
            setBusy(false);
        }
    }
    return (_jsxs("main", { id: "main", className: "mx-auto max-w-3xl px-6 py-10", children: [_jsx("h1", { className: "text-2xl font-semibold", children: name }), _jsxs("p", { className: "mt-1 text-sm text-slate-500", children: ["Sending to: ", _jsx("strong", { children: cfg.sheetName }), "."] }), _jsxs("div", { className: "mt-6 flex items-center gap-3", children: [_jsx(Button, { disabled: busy, onClick: sendAll, children: busy ? 'Sending…' : 'Send last 50 reviews now' }), count !== null && _jsxs("span", { className: "text-sm text-slate-500", children: ["Last run: ", count, " rows."] })] }), _jsx("p", { className: "mt-6 text-xs text-slate-500", children: "New reviews go to your sheet automatically too \u2014 you don't have to click this button unless you want a manual resend." })] }));
}
const runtimeHooks = {
    async onReviewCreated(ctx, review) {
        const r = review;
        const cfg = ctx.config;
        if (!cfg.webhookUrl)
            return;
        if (!ctx.permissions.includes('external:fetch'))
            return;
        await fetch(cfg.webhookUrl, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
                id: r.id, platform: r.platform, author: r.author_name,
                rating: r.rating, body: r.body, posted_at: r.posted_at,
                source: 'plugin:builder:export-google-sheets',
            }),
        }).catch(() => { });
    },
};
const generate = (params) => {
    const cfg = params.config;
    const manifest = manifestFor({
        instanceId: params.instanceId, name: params.instanceName,
        description: `Sends every new review to the "${cfg.sheetName}" Google Sheet.`,
        category: 'integration', icon: 'Sheet',
        permissions: ['read:reviews', 'external:fetch', 'ui:toast'],
        entry: 'both', hooks: { onReviewCreated: true },
        config: meta.configSchema,
        defaultConfig: { webhookUrl: cfg.webhookUrl, sheetName: cfg.sheetName },
    });
    const pluginTsx = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import { useState } from 'react';
import { useReviews, useToast } from '../sdk';
import { Button } from '../../ui/Button';

export default function ${cls(params.instanceId)}() {
  const { reviews } = useReviews({ limit: 50 });
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  async function sendAll() {
    setBusy(true);
    try {
      for (const r of (reviews ?? [])) {
        await fetch('${esc(cfg.webhookUrl)}', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ id: r.id, platform: r.platform, author: r.author_name, rating: r.rating, body: r.body, posted_at: r.posted_at }),
        }).catch(() => {});
      }
      toast.success(\`Sent \${(reviews ?? []).length} rows to ${esc(cfg.sheetName)}.\`);
    } finally { setBusy(false); }
  }
  return (
    <main id="main" className="mx-auto max-w-3xl px-6 py-10">
      <h1 className="text-2xl font-semibold">${esc(params.instanceName)}</h1>
      <p className="mt-1 text-sm text-slate-500">Sending to: <strong>${esc(cfg.sheetName)}</strong></p>
      <div className="mt-6"><Button disabled={busy} onClick={sendAll}>{busy ? 'Sending…' : 'Send last 50 now'}</Button></div>
    </main>
  );
}
`;
    const hooksTs = header({ name: params.instanceName, templateId: meta.id, instanceId: params.instanceId }) +
        `import type { PluginHandlers, HandlerContext } from '../types';

export const handlers: PluginHandlers = {
  async onReviewCreated(ctx: HandlerContext, review: unknown) {
    const r = review as { id: string; rating: number | null; body: string | null; author_name: string | null; platform: string; posted_at: string | null };
    if (!ctx.permissions.includes('external:fetch')) return;
    await fetch('${esc(cfg.webhookUrl)}', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id: r.id, platform: r.platform, author: r.author_name, rating: r.rating, body: r.body, posted_at: r.posted_at }),
    }).catch(() => {});
  },
};
export default handlers;
`;
    return assemble({
        manifest, pluginTsx, hooksTs,
        readmeMd: readme({
            name: params.instanceName, templateId: meta.id,
            description: `Pushes every new review to the "${cfg.sheetName}" Google Sheet via your Zapier webhook.`,
            configSummary: [['webhookUrl', cfg.webhookUrl], ['sheetName', cfg.sheetName]],
        }),
    });
};
function esc(s) { return safeString(String(s)); }
function cls(id) { return 'P_' + id.replace(/[^a-zA-Z0-9]+/g, '_'); }
const template = {
    id: meta.id, name: meta.name, tagline: meta.tagline, icon: meta.icon,
    category: meta.category,
    permissions: meta.permissions,
    permissionSummary: meta.permissionSummary,
    entry: meta.entry,
    hooks: meta.hooks,
    configSchema: meta.configSchema,
    defaultConfig: meta.defaultConfig,
    suggestName: (cfg) => `Reviews → ${cfg.sheetName || 'Google Sheet'}`,
    describe: (cfg) => `Send every new review to the Google Sheet named "${cfg.sheetName || 'Reviews'}" using your Zapier webhook.`,
    generate, runtimeUi: RuntimeUi, runtimeHooks,
    clickCount: meta.clickCount,
};
export default template;
